/** iPhone / iPad (including iPadOS-as-Mac and iPhone “desktop website”). */
export const isIosAuthHostFor = (input: {
  userAgent: string
  platform: string
  maxTouchPoints: number
}): boolean => {
  if (/iPhone|iPod|iPad/.test(input.userAgent)) return true
  return (
    (input.platform === 'MacIntel' || /Macintosh/.test(input.userAgent)) &&
    input.maxTouchPoints > 1
  )
}

export const isIosAuthHost = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return isIosAuthHostFor({
    userAgent: navigator.userAgent || '',
    platform: navigator.platform || '',
    maxTouchPoints: navigator.maxTouchPoints || 0,
  })
}

/** Firebase Hosting preview channels: `{site}--{channel}-{hash}.web.app`. */
export const isHostingPreviewChannel = (hostname: string): boolean =>
  hostname.endsWith('.web.app') && hostname.includes('--')

/**
 * Origins that are safe as a Google redirect continue URL.
 * Preview channels and leftover `*.web.app` hosts are not — those made
 * Google show "The requested action was invalid".
 */
export const isRedirectSafeOrigin = (hostname: string, authDomainHost = ''): boolean => {
  if (!hostname) return false
  if (isHostingPreviewChannel(hostname)) return false
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true
  if (hostname.endsWith('.optictruckworks.com')) return true
  return Boolean(authDomainHost) && hostname === authDomainHost
}

export type GoogleRedirectInput = {
  protocol: string
  hostname: string
  isIos: boolean
  standalone: boolean
  authDomainHost?: string
}

/**
 * Redirect only on HTTPS first-party portal hosts (or the authDomain host).
 * Preview channels, LAN HTTP, and other `*.web.app` hosts keep the popup
 * so OAuth stays on authDomain.
 *
 * iOS Safari / installed PWA block or break `signInWithPopup`; use redirect
 * there once authDomain is the custom portal host (not firebaseapp).
 */
export const shouldUseGoogleRedirectFor = (input: GoogleRedirectInput): boolean => {
  if (input.protocol !== 'https:') return false
  if (!isRedirectSafeOrigin(input.hostname, input.authDomainHost || '')) return false
  return input.isIos || input.standalone
}

export const shouldUseGoogleRedirect = (): boolean => {
  if (typeof window === 'undefined') return false
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  return shouldUseGoogleRedirectFor({
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    isIos: isIosAuthHost(),
    standalone: Boolean(standalone),
    authDomainHost: process.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  })
}
